import { api } from './client'
import type { CostCenter } from './deconturi'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Financiar/ActiveFixeModels.cs ----------

export type AssetCategory = 'EchipamentProductie' | 'UtilajAuxiliar' | 'Autovehicul' | 'IT' | 'Mobilier' | 'ConstructieAmenajare' | 'Matrita'
export const ASSET_CATEGORIES: { value: AssetCategory; label: string }[] = [
  { value: 'EchipamentProductie', label: 'Echipament de producție' },
  { value: 'UtilajAuxiliar', label: 'Utilaj auxiliar' },
  { value: 'Autovehicul', label: 'Autovehicul' },
  { value: 'IT', label: 'IT' },
  { value: 'Mobilier', label: 'Mobilier' },
  { value: 'ConstructieAmenajare', label: 'Construcție / amenajare' },
  { value: 'Matrita', label: 'Matriță' },
]

export type AssetDepreciationMethod = 'Liniara' | 'Degresiva' | 'Accelerata'
export const DEPRECIATION_METHODS: { value: AssetDepreciationMethod; label: string }[] = [
  { value: 'Liniara', label: 'Liniară' },
  { value: 'Degresiva', label: 'Degresivă' },
  { value: 'Accelerata', label: 'Accelerată' },
]

export type AssetStatus = 'InCursDePunereInFunctiune' | 'Functional' | 'InMentenanta' | 'InConservare' | 'Casat' | 'Vandut'

export type AssetCostComponentType = 'Achizitie' | 'Transport' | 'Montaj' | 'PunereInFunctiune' | 'TaxeVamale' | 'Altele'
export const COST_COMPONENT_TYPES: { value: AssetCostComponentType; label: string }[] = [
  { value: 'Achizitie', label: 'Achiziție' },
  { value: 'Transport', label: 'Transport' },
  { value: 'Montaj', label: 'Montaj' },
  { value: 'PunereInFunctiune', label: 'Punere în funcțiune' },
  { value: 'TaxeVamale', label: 'Taxe vamale' },
  { value: 'Altele', label: 'Altele' },
]

export type AssetPurchaseRequestStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Rejected'

export interface AssetTransfer { id: string; date: string; fromLocation: string; toLocation: string; reason: string | null }
export interface AssetInventoryCountEntry { id: string; date: string; countedByUserId: string; countedByUserName: string; found: boolean; notes: string | null }
export interface AssetCostComponent { id: string; type: AssetCostComponentType; description: string; amount: number; supplierInvoiceId: string | null; addedAt: string }

export interface AssetListItem {
  id: string; inventoryNumber: string; name: string; category: AssetCategory; subcategory: string | null
  acquisitionDate: string; entryValue: number; location: string | null; costCenter: CostCenter
  status: AssetStatus; commissionedAt: string | null; accumulatedDepreciation: number; netBookValue: number
}

export interface AssetDetail extends AssetListItem {
  supplierId: string | null; supplierName: string | null; invoiceNumber: string | null
  depreciationDurationMonths: number; depreciationMethod: AssetDepreciationMethod; residualValue: number
  responsibleUserId: string | null; responsibleUserName: string | null
  retiredAt: string | null; assetPurchaseRequestId: string | null
  serialNumber: string | null; warrantyUntil: string | null; notes: string | null
  transfers: AssetTransfer[]; inventoryCounts: AssetInventoryCountEntry[]; costComponents: AssetCostComponent[]
}

export interface SaveAssetPayload {
  name: string; category: AssetCategory; subcategory: string | null; acquisitionDate: string; entryValue: number
  supplierId: string | null; invoiceNumber: string | null; depreciationDurationMonths: number
  depreciationMethod: AssetDepreciationMethod; residualValue: number; location: string | null
  responsibleUserId: string | null; costCenter: CostCenter; serialNumber: string | null
  warrantyUntil: string | null; notes: string | null
}

export interface RetireAssetResult { pendingApproval: boolean }

export const listAssets = () => api.get<AssetListItem[]>('/active/registru').then((r) => r.data)
export const getAsset = (id: string) => api.get<AssetDetail>(`/active/registru/${id}`).then((r) => r.data)
export const createAsset = (payload: SaveAssetPayload) =>
  api.post<{ id: string }>('/active/registru', payload).then((r) => r.data)
export const updateAsset = (id: string, payload: SaveAssetPayload) => api.put(`/active/registru/${id}`, payload)
export const transferAsset = (id: string, date: string, toLocation: string, reason: string | null) =>
  api.post(`/active/registru/${id}/transfer`, { date, toLocation, reason })
export const recordAssetInventoryCount = (id: string, date: string, found: boolean, notes: string | null) =>
  api.post(`/active/registru/${id}/inventariere`, { date, found, notes })
export const retireAsset = (id: string, reason: string) =>
  api.post<RetireAssetResult>(`/active/registru/${id}/casare`, { reason }).then((r) => r.data)
export const getAssetExportUrl = () => api.get<Blob>('/active/registru/export', { responseType: 'blob' }).then((r) => r.data)

// ---------- FR-FAF-013…016 — intrări de active fixe (cereri de achiziție, generare din factură, PIF) ----------

export interface AssetPurchaseRequestListItem {
  id: string; number: string; date: string; requestedByUserName: string; name: string; category: AssetCategory
  costCenter: CostCenter; estimatedValue: number; currency: string; status: AssetPurchaseRequestStatus
}

export interface AssetPurchaseRequestDetail {
  id: string; number: string; date: string; requestedByUserId: string; requestedByUserName: string
  name: string; category: AssetCategory; costCenter: CostCenter; estimatedValue: number; currency: string
  justification: string; status: AssetPurchaseRequestStatus; notes: string | null
}

export interface SaveAssetPurchaseRequestPayload {
  date: string; name: string; category: AssetCategory; costCenter: CostCenter
  estimatedValue: number; currency: string; justification: string; notes: string | null
}

export interface CreateAssetFromInvoicePayload {
  supplierInvoiceId: string; supplierInvoiceLineIds: string[]
  name: string; category: AssetCategory; subcategory: string | null; depreciationDurationMonths: number
  depreciationMethod: AssetDepreciationMethod; residualValue: number; location: string | null
  responsibleUserId: string | null; costCenter: CostCenter; serialNumber: string | null
  warrantyUntil: string | null; notes: string | null; assetPurchaseRequestId: string | null
}

export const listAssetPurchaseRequests = () => api.get<AssetPurchaseRequestListItem[]>('/active/intrari').then((r) => r.data)
export const getAssetPurchaseRequest = (id: string) => api.get<AssetPurchaseRequestDetail>(`/active/intrari/${id}`).then((r) => r.data)
export const createAssetPurchaseRequest = (payload: SaveAssetPurchaseRequestPayload) =>
  api.post<{ id: string }>('/active/intrari', payload).then((r) => r.data)
export const updateAssetPurchaseRequest = (id: string, payload: SaveAssetPurchaseRequestPayload) => api.put(`/active/intrari/${id}`, payload)
export const submitAssetPurchaseRequest = (id: string) =>
  api.post<{ pendingApproval: boolean }>(`/active/intrari/${id}/submit-aprobare`).then((r) => r.data)

export const listAssetsInProgress = () => api.get<AssetListItem[]>('/active/intrari/active-in-curs').then((r) => r.data)
export const createAssetFromInvoice = (payload: CreateAssetFromInvoicePayload) =>
  api.post<{ id: string }>('/active/intrari/din-factura', payload).then((r) => r.data)
export const addAssetCostComponent = (assetId: string, type: AssetCostComponentType, description: string, amount: number, supplierInvoiceId: string | null) =>
  api.post(`/active/intrari/${assetId}/costuri-suplimentare`, { type, description, amount, supplierInvoiceId })
export const commissionAsset = (assetId: string, date: string) =>
  api.post(`/active/intrari/${assetId}/punere-in-functiune`, { date })

// ---------- FR-FAF-018…023 — vânzare/cedare active fixe ----------

export interface SellAssetPayload {
  assetId: string; date: string; buyerClientId: string | null; newBuyerName: string | null
  salePrice: number; currency: string; vatRateId: string | null
}
export interface SellAssetResult { pendingApproval: boolean }

export interface AssetExit {
  assetId: string; inventoryNumber: string; assetName: string; exitType: 'Casare' | 'Vanzare'; date: string
  counterpartyOrReason: string; amount: number | null; gainLoss: number | null; invoiceNumber: string | null
}

export type AssetMovementType = 'Intrare' | 'CostSuplimentar' | 'PunereInFunctiune' | 'Transfer' | 'Inventariere' | 'Casare' | 'Vanzare'
export interface AssetMovement { type: AssetMovementType; date: string; description: string; amount: number | null }

export const sellAsset = (payload: SellAssetPayload) =>
  api.post<SellAssetResult>('/active/iesiri/vanzare', payload).then((r) => r.data)
export const listAssetExits = () => api.get<AssetExit[]>('/active/iesiri').then((r) => r.data)
export const getAssetExitsExportUrl = () => api.get<Blob>('/active/iesiri/export', { responseType: 'blob' }).then((r) => r.data)
export const getAssetMovements = (assetId: string) => api.get<AssetMovement[]>(`/active/registru/${assetId}/miscari`).then((r) => r.data)

// ---------- FR-FAF-002, 010, 011 — amortizare ----------

export interface AssetDepreciationSummaryItem {
  id: string; inventoryNumber: string; name: string; category: AssetCategory; costCenter: CostCenter
  entryValue: number; accumulatedDepreciation: number; netBookValue: number
  currentPeriodDepreciation: number; yearToDateDepreciation: number; status: AssetStatus
}
export interface AssetDepreciationSummary {
  asOf: string; currentPeriodFrom: string; yearStart: string
  totalCurrentPeriodDepreciation: number; totalYearToDateDepreciation: number
  items: AssetDepreciationSummaryItem[]
}
export interface AssetDepreciationScheduleEntry {
  periodNumber: number; periodEnd: string; openingNetBookValue: number
  periodDepreciation: number; accumulatedDepreciation: number; closingNetBookValue: number; isElapsed: boolean
}
export interface AssetDepreciationSchedule {
  assetId: string; inventoryNumber: string; name: string; method: AssetDepreciationMethod
  depreciationDurationMonths: number; entryValue: number; residualValue: number
  commissionedAt: string | null; entries: AssetDepreciationScheduleEntry[]
}

export const getAssetDepreciationSummary = () => api.get<AssetDepreciationSummary>('/active/amortizare').then((r) => r.data)
export const getAssetDepreciationSchedule = (assetId: string) =>
  api.get<AssetDepreciationSchedule>(`/active/amortizare/${assetId}/scadentar`).then((r) => r.data)
